import React from "react";
import {Card, Container} from "react-bootstrap";
import {PiCircleFill} from "react-icons/pi";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useNutzungLive from "../../Components/Nutzung/useNutzungLive.js";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";
import NutzungMitarbeiterBereiche from "../../Components/Nutzung/NutzungMitarbeiterBereiche.jsx";
import NutzungKennzahlen from "../../Components/Nutzung/NutzungKennzahlen.jsx";
import NutzungVerlaufChart from "../../Components/Nutzung/NutzungVerlaufChart.jsx";
import NutzungHeatmap from "../../Components/Nutzung/NutzungHeatmap.jsx";
import NutzungBereichsverteilung from "../../Components/Nutzung/NutzungBereichsverteilung.jsx";
import NutzungMitarbeiterRangliste from "../../Components/Nutzung/NutzungMitarbeiterRangliste.jsx";
import {bereichFarbe, bereichLabel, formatTimeDE} from "../../Components/Nutzung/format.js";
import {seitenName} from "../../Components/Nutzung/routeLabels.js";
import {MitarbeiterLink} from "../../Components/Nutzung/Links.jsx";

/** "vor 3 Min." statt eines vollen Zeitstempels - dieselbe Kurzform wie in
 * NutzungLiveListe (bewusst hier dupliziert statt importiert: diese Datei
 * gehört nicht zum Umbau-Auftrag, siehe Auftragsbeschreibung). */
function vorMinuten(iso) {
    if (!iso) {
        return "–";
    }
    const diffMs = Date.now() - new Date(iso).getTime();
    const minuten = Math.max(0, Math.round(diffMs / 60000));
    if (minuten < 1) {
        return "gerade eben";
    }
    if (minuten === 1) {
        return "vor 1 Min.";
    }
    return `vor ${minuten} Min.`;
}

/**
 * Live-Liste "Wer ist online" - Kernanforderung 2 der Spezifikation: der
 * Mitarbeitername ist hier direkt anklickbar (MitarbeiterLink), Bereich
 * bekommt zusätzlich die einheitliche Bereichsfarbe als kleine Marke, damit
 * dieselbe Farbe wie im Rest der Auswertung (Wegverlauf, Tagesband,
 * Bereichsverteilung) wiedererkennbar bleibt.
 */
function LiveListe({eintraege}) {
    const rows = Array.isArray(eintraege) ? eintraege : [];
    const aktive = rows.filter((r) => r.aktiv).length;

    return (
        <div className="d-flex flex-column gap-2">
            <div className="small text-muted">
                {aktive} von {rows.length} {rows.length === 1 ? "Person" : "Personen"} gerade aktiv.
            </div>
            {rows.map((eintrag) => (
                <Card key={eintrag.user_id} className="shadow-sm">
                    <Card.Body className="py-2 d-flex align-items-center flex-wrap gap-3">
                        <span aria-hidden="true">
                            <PiCircleFill size={12} color={eintrag.aktiv ? "#2f9e44" : "#adb5bd"} />
                        </span>
                        <div style={{minWidth: 170}}>
                            <div className="fw-semibold">
                                <MitarbeiterLink id={eintrag.user_id} name={eintrag.name} />
                            </div>
                            <div className="small text-muted">{eintrag.aktiv ? "Aktiv" : "Anwesend"}</div>
                        </div>
                        <div className="flex-grow-1">
                            <div>{seitenName(eintrag.seite)}</div>
                            <div className="small text-muted d-inline-flex align-items-center gap-1">
                                <span
                                    aria-hidden="true"
                                    style={{width: 8, height: 8, borderRadius: 2, background: bereichFarbe(eintrag.bereich), display: "inline-block"}}
                                />
                                {bereichLabel(eintrag.bereich)}
                            </div>
                        </div>
                        <div className="text-end small text-muted" style={{minWidth: 150}}>
                            <div>Angemeldet seit {formatTimeDE(eintrag.seit)} Uhr</div>
                            <div>Letzte Aktion {vorMinuten(eintrag.letzte_aktion)}</div>
                        </div>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
}

/**
 * Einstiegsseite der Nutzungsauswertung ("Jetzt" - Wer ist online).
 * Schwerpunkt bleibt weiterhin die Live-Liste ganz oben (Thorstens
 * Wortlaut: "ich sehe aktuell gar nicht, in welchem Bereich welcher
 * Mitarbeiter arbeitet und auch nicht, wo genau er sich gerade befindet"),
 * jetzt zusätzlich mit anklickbaren Namen. Kennzahlen, Verlauf und Muster
 * bleiben erhalten, rücken aber bewusst nach unten.
 */
export default function NutzungUeberblick() {
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;
    const live = useNutzungLive();

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Jetzt" titel="Wer ist online" nutzungFilters={nutzungFilters}>
                <div className="d-flex flex-column gap-3">
                    {/* "Gerade im System" hängt bewusst NICHT am Zeitraum-Filter der
                        Seite - "jetzt" kennt kein Von/Bis, siehe useNutzungLive. */}
                    <NutzungWidget
                        title="Gerade im System"
                        help={
                            'Aktualisiert sich automatisch alle 15 Sekunden, solange dieses Fenster ' +
                            'sichtbar ist. „Aktiv" heißt: eine echte Aktion in den letzten 2 Minuten, ' +
                            '„Anwesend" heißt nur, dass das Fenster geöffnet ist.'
                        }
                        loading={live.loading}
                        error={live.error}
                        empty={!live.loading && !live.error && live.rows.length === 0}
                        height={Math.max(120, live.rows.length * 76)}
                        actions={
                            live.stand && (
                                <span className="small text-muted">Stand: {formatTimeDE(live.stand.toISOString())} Uhr</span>
                            )
                        }
                    >
                        <LiveListe eintraege={live.rows} />
                    </NutzungWidget>

                    <NutzungMitarbeiterBereiche filters={filters} />

                    <NutzungKennzahlen filters={filters} />

                    <NutzungVerlaufChart filters={filters} />

                    <div className="row g-3">
                        <div className="col-lg-7">
                            <NutzungHeatmap filters={filters} />
                        </div>
                        <div className="col-lg-5">
                            <NutzungBereichsverteilung filters={filters} />
                        </div>
                    </div>

                    <NutzungMitarbeiterRangliste filters={filters} />
                </div>
            </NutzungLayout>
        </Container>
    );
}
